/** Mensajes del chat flotante de Sol (extraídos de GlobalRagChat sin cambios). */
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { ArrowUpRight, FileText, Loader2, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { SolHtmlInline, SOL_CONTENT_RESPONSIVE } from '@/components/sol/SolHtmlInline';
import { cn } from '@/lib/utils';
import { MarcasDeAdjuntos } from '@/features/sol/components/AdjuntosDeMensaje';
import { isDocCitation, isProspectMatch, listaDeCitas, type ChatMessage, type Citation } from '@/services/ragChatService';

import { SolAvatar } from './SolAvatar';

// ────────────────────────────────────────────────────────────────────────────
// Mensajes
// ────────────────────────────────────────────────────────────────────────────

export function MessageBubble({ message }: { message: ChatMessage }) {
    const isUser = message.role === 'user';
    return (
        <div className={cn('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
            {!isUser && (
                <div className="text-muted-foreground inline-flex items-center gap-1 text-micro">
                    <SolAvatar size="sm" />
                    <span className="font-medium text-foreground/80">Sol</span>
                </div>
            )}
            {isUser && <MarcasDeAdjuntos citations={message.citations} />}
            {(!isUser || message.content) && (
            <div
                className={cn(
                    'max-w-[90%] rounded-lg px-2.5 py-1.5 text-body leading-snug sm:text-body',
                    isUser
                        ? 'bg-primary/15 text-foreground border-primary/25 border [overflow-wrap:anywhere] whitespace-pre-wrap'
                        : cn(
                              'bg-surface-lo text-foreground/90 border-border-dim border [&_a]:text-primary [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-caption',
                              SOL_CONTENT_RESPONSIVE,
                          ),
                )}
            >
                {isUser ? (
                    message.content
                ) : (
                    // SolHtmlInline aplica renderSolHtmlSafe + intercepta clicks
                    // en <a data-sol-internal> para usar react-router (sin reload).
                    // Anexo C · Fase C7.
                    <SolHtmlInline as="span" text={message.content} />
                )}
            </div>
            )}
            {listaDeCitas(message.citations).length > 0 && (
                <CitationList citations={listaDeCitas(message.citations)} />
            )}
            <span className="text-muted-foreground/60 text-micro">
                {formatDistanceToNow(new Date(message.created_at), {
                    addSuffix: true,
                    locale: es,
                })}
                {message.model && ` · ${message.model}`}
            </span>
        </div>
    );
}

/**
 * Lista de citations debajo del mensaje del assistant.
 *
 * Hoy hay 2 tipos:
 *   - DocCitation (chunk_id): ancla al doc en la página actual (#doc-ID).
 *   - ProspectMatch (prospect_id): Link a /prospects/:id — abre el detail.
 *
 * Cuando el LLM recomienda un prospect en su respuesta, el user puede
 * tocar el chip y caer directamente en su detail page.
 */
function CitationList({ citations }: { citations: Citation[] }) {
    const docs = citations.filter(isDocCitation);
    const prospects = citations.filter(isProspectMatch);

    if (docs.length === 0 && prospects.length === 0) return null;

    return (
        <div className="flex max-w-[95%] flex-col gap-1.5">
            {prospects.length > 0 && (
                <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground inline-flex items-center gap-1 text-micro">
                        <Users className="h-2.5 w-2.5" /> Prospects relevantes
                    </span>
                    <div className="flex flex-wrap gap-1">
                        {prospects.slice(0, 5).map((p) => (
                            <Link
                                key={p.prospect_id}
                                to={`/prospects/${p.prospect_id}`}
                                className="border-primary/30 bg-primary/10 text-foreground hover:bg-primary/20 hover:border-primary/50 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-micro font-medium transition-colors"
                                title={`Abrir ${p.name}${p.sector ? ' · ' + p.sector : ''}`}
                            >
                                <span className="truncate max-w-[160px]">{p.name}</span>
                                {p.sector && (
                                    <span className="text-muted-foreground text-micro">
                                        · {p.sector}
                                    </span>
                                )}
                                <ArrowUpRight className="text-primary h-3 w-3 shrink-0" />
                            </Link>
                        ))}
                        {prospects.length > 5 && (
                            <span className="text-muted-foreground/60 self-center text-micro">
                                +{prospects.length - 5}
                            </span>
                        )}
                    </div>
                </div>
            )}
            {docs.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {docs.slice(0, 4).map((c, i) => (
                        <a
                            key={c.chunk_id}
                            href={`#doc-${c.document_id}`}
                            className="border-border-dim bg-surface-hi text-muted-foreground hover:text-foreground inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-micro transition-colors"
                            title={`${c.document_title} · ${c.document_kind} · sim ${c.similarity.toFixed(2)}`}
                        >
                            <FileText className="h-2.5 w-2.5" />
                            [doc {i + 1}] {c.document_title.slice(0, 28)}
                            {c.document_title.length > 28 ? '…' : ''}
                        </a>
                    ))}
                    {docs.length > 4 && (
                        <span className="text-muted-foreground/60 text-micro">
                            +{docs.length - 4} más
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

export function AssistantThinking({ texto = 'Sol está pensando…' }: { texto?: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <SolAvatar size="sm" />
            <div className="bg-surface-lo text-muted-foreground border-border-dim inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-body">
                <Loader2 className="h-3 w-3 animate-spin" />
                {texto}
            </div>
        </div>
    );
}

export function EmptyHint({
    isProspect,
    prospectName,
}: {
    isProspect: boolean;
    prospectName?: string;
}) {
    if (isProspect) {
        return (
            <div className="border-border bg-surface-hi flex flex-col items-center gap-2 rounded-md border border-dashed p-4 text-center">
                <SolAvatar size="lg" />
                <p className="text-foreground/85 text-body font-semibold">
                    Hola, soy Sol.
                </p>
                <p className="text-muted-foreground text-caption leading-snug">
                    Te ayudo con todo lo de <strong>{prospectName ?? 'este prospect'}</strong>.
                    Tengo a mano los documentos cargados y los datos del CRM.
                    Probá: <em>"¿qué dolor tiene?"</em> ·{' '}
                    <em>"¿qué propuesta le mandamos?"</em> ·{' '}
                    <em>"¿cómo encaro la próxima llamada?"</em>
                </p>
            </div>
        );
    }
    return (
        <div className="border-border bg-surface-hi flex flex-col items-center gap-2 rounded-md border border-dashed p-4 text-center">
            <SolAvatar size="lg" />
            <p className="text-foreground/85 text-body font-semibold">Hola, soy Sol.</p>
            <p className="text-muted-foreground text-caption leading-snug">
                Modo libre. Puedo buscar en cualquier documento que tengas accesible.
                Cuando entres a un prospect específico, voy a usar solo sus docs.
            </p>
        </div>
    );
}
